import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import axiosInstance from "../../utils/axiosConfig";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import CopyToClipboard from "react-copy-to-clipboard";
import toast from "react-hot-toast";
import { TailSpin } from "react-loader-spinner";
import { TiWarning } from "react-icons/ti";

import { API_URL } from "../../constant";
import { useAuth } from "../../context/auth-context";
import DocumentLightbox from "../../components/DocumentLightbox";
import type { AdminGiftcardTransaction } from "../../lib/api/adminTransactionsData";
import { apiQueryKey } from "../../lib/api/queryKeys";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "../orders/ordersOrderTable";
import {
  computeGiftcardNgnAndUsd,
  getGiftcardDisplayDate,
  giftcardCardName,
  giftcardEcode,
  giftcardImageUrls,
  giftcardTypeLabel,
} from "./giftcardTradeMath";

type Props = {
  open: boolean;
  onClose: () => void;
  transaction: AdminGiftcardTransaction | null;
  banksByCode: Map<string, string>;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function formatTs(iso: unknown): string {
  const s = str(iso);
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return s;
  }
}

type UserShape = {
  email?: { address?: string };
  profile?: { firstName?: string; lastName?: string };
  banks?: Array<{
    accountNumber?: string | number;
    bankCode?: string | number;
    accountName?: string;
    bankName?: string;
    isDefault?: boolean;
  }>;
};

function resolveBankName(
  bank: NonNullable<UserShape["banks"]>[number],
  banksByCode: Map<string, string>
): string {
  const directName = str(bank?.bankName).trim();
  if (directName) return directName;

  const rawCode = bank?.bankCode != null ? String(bank.bankCode).trim() : "";
  if (!rawCode) return "—";

  const normalizedNumeric = /^\d+$/.test(rawCode)
    ? String(Number(rawCode))
    : rawCode;
  const candidates = [
    rawCode,
    normalizedNumeric,
    normalizedNumeric.padStart(3, "0"),
    rawCode.padStart(3, "0"),
  ];

  for (const c of candidates) {
    const name = banksByCode.get(c);
    if (name) return name;
  }
  return "—";
}

async function requestSignedImageUrl(
  objectURL: string,
  isAuthenticated: string
): Promise<string> {
  const { data } = await axiosInstance.post<{
    data?: { signedURL?: string };
  }>(`${API_URL}api/gift-cards-transactions/images/request-download-url`, {
    objectURL,
  });
  const url = data?.data?.signedURL;
  if (typeof url === "string" && url.length > 0) return url;
  return objectURL;
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[#2C363E] bg-[#161C22]/55 p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.035)] sm:p-4">
      <h3 className="mb-3 flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#869AA9]">
        <span
          className="h-px w-7 shrink-0 bg-gradient-to-r from-[#4FB0FF]/90 to-transparent"
          aria-hidden
        />
        {title}
      </h3>
      <div className="space-y-0 divide-y divide-[#23292F]">{children}</div>
    </section>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-3 first:pt-0 last:pb-0 sm:flex-row sm:justify-between sm:gap-4 sm:items-start">
      <dt className="text-[12px] font-medium text-[#7D8F9C] shrink-0 min-w-[7.5rem]">
        {label}
      </dt>
      <dd className="min-w-0 text-[13px] font-medium leading-snug text-[#EEF2F6] text-left sm:text-right break-words">
        {value}
      </dd>
    </div>
  );
}

function CopyChip({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void onCopy();
      }}
      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-[#3A4852]/80 bg-[#0D1114]/80 px-2.5 py-1.5 text-left transition hover:border-[#4FB0FF]/40 hover:bg-[#1C2227]"
    >
      <span className="min-w-0 truncate font-mono text-[11px] leading-snug text-[#C6D7E4]">
        {text}
      </span>
      <span className="shrink-0 rounded border border-[#3A4852] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#AEBCC6]">
        {copied ? "Done" : "Copy"}
      </span>
    </button>
  );
}

const AdminGiftcardOrderModal = ({
  open,
  onClose,
  transaction,
  banksByCode,
}: Props) => {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [phase, setPhase] = useState<"review" | "confirm">("review");
  const [signedUrls, setSignedUrls] = useState<string[]>([]);
  /** Index of the proof image open in the lightbox, or null when closed. */
  const [proofIndex, setProofIndex] = useState<number | null>(null);
  const [signingImages, setSigningImages] = useState(false);
  const [copyMail, setCopyMail] = useState(false);
  const [copyAmount, setCopyAmount] = useState(false);
  const [copyAccount, setCopyAccount] = useState(false);
  const [copyEcode, setCopyEcode] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const userData = useMemo((): UserShape | null => {
    if (!transaction?.user || typeof transaction.user !== "object") return null;
    return transaction.user as UserShape;
  }, [transaction?.user]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => closeRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || !transaction || !isAuthenticated) {
      setSignedUrls([]);
      return;
    }
    const raw = giftcardImageUrls(transaction);
    if (raw.length === 0) {
      setSignedUrls([]);
      return;
    }
    let cancelled = false;
    setSigningImages(true);
    (async () => {
      const out: string[] = [];
      for (const u of raw) {
        try {
          out.push(await requestSignedImageUrl(u, isAuthenticated));
        } catch {
          out.push(u);
        }
      }
      if (!cancelled) {
        setSignedUrls(out);
        setSigningImages(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, transaction, isAuthenticated]);

  useEffect(() => {
    if (open) setPhase("review");
  }, [open, transaction?._id]);

  const tx = transaction;
  const userId = tx ? str(tx.userId) : "";
  const math = useMemo(
    () =>
      tx
        ? computeGiftcardNgnAndUsd(tx)
        : { ngn: 0, usd: 0, rateMatched: false },
    [tx]
  );

  const defaultBank = useMemo(() => {
    const banks = userData?.banks;
    if (!Array.isArray(banks)) return null;
    return banks.find((b) => b?.isDefault) ?? banks[0] ?? null;
  }, [userData?.banks]);

  const bankName = defaultBank
    ? resolveBankName(defaultBank, banksByCode)
    : "—";

  const email = userData?.email?.address?.trim() ?? "";
  const firstName = userData?.profile?.firstName?.trim() ?? "";
  const lastName = userData?.profile?.lastName?.trim() ?? "";
  const displayName = `${firstName} ${lastName}`.trim() || "—";

  const statusUpper = tx ? str(tx.status).toUpperCase() : "";
  const isPending = statusUpper === "PENDING" || statusUpper === "IN PROGRESS";
  const isCompleted =
    statusUpper === "COMPLETED" || statusUpper === "SUCCESSFUL";

  const ecode = tx ? giftcardEcode(tx) : "";
  const cardName = tx ? giftcardCardName(tx) : "";

  const cardCatalogId = useMemo(() => {
    if (!tx?.card || typeof tx.card !== "object") return "";
    const c = tx.card as { id?: unknown; _id?: unknown };
    return str(c.id) || str(c._id);
  }, [tx]);

  const tradeLegs = useMemo(() => {
    const tv = tx?.tradeValue;
    if (!Array.isArray(tv)) return [];
    return tv.map((leg, i) => {
      if (!leg || typeof leg !== "object")
        return { key: `leg-${i}`, min: "—", max: "—", amount: "—" };
      const r = (leg as { range?: { min?: unknown; max?: unknown } }).range;
      const amount = num((leg as { amount?: unknown }).amount);
      return {
        key: str((leg as { _id?: unknown })._id) || `leg-${i}`,
        min: r?.min != null ? String(r.min) : "—",
        max: r?.max != null ? String(r.max) : "—",
        amount: amount != null ? formatAmount(amount) : "—",
      };
    });
  }, [tx]);

  const apiTotalTrade = tx ? num(tx.totalTradeValue) : undefined;
  const userMongoId =
    tx?.user && typeof tx.user === "object"
      ? str((tx.user as { _id?: unknown })._id)
      : "";

  const idShort =
    tx && tx._id.length > 14
      ? `${tx._id.slice(0, 8)}…${tx._id.slice(-4)}`
      : tx?._id ?? "";

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!tx || !isAuthenticated) throw new Error("Missing data");
      const { ngn, usd } = computeGiftcardNgnAndUsd(tx);
      const name = giftcardCardName(tx);
      await axiosInstance.post(`${API_URL}api/payouts`, {
        asset: { name, amountInUSD: usd },
        payoutAmount: ngn,
        userId: str(tx.userId),
        transaction: { id: tx._id, type: "GIFT-CARD" },
      });
      await axiosInstance.patch(
        `${API_URL}api/gift-cards-transactions/${tx._id}`,
        { status: "COMPLETED" }
      );
    },
    onSuccess: () => {
      toast.success("Trade marked as paid; manual payout recorded.");
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey("admin-transactions-data"),
      });
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey("admin-transactions-summaries"),
      });
      onClose();
    },
    onError: (e: unknown) => {
      const msg =
        axios.isAxiosError(e) &&
        e.response?.data &&
        typeof e.response.data === "object" &&
        "message" in e.response.data
          ? String((e.response.data as { message?: string }).message)
          : "Could not mark as paid.";
      toast.error(msg);
    },
  });
  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!tx || !isAuthenticated) throw new Error("Missing data");
      const reason = rejectReason.trim();
      if (!reason) throw new Error("Missing rejection reason");
      await axiosInstance.patch(
        `${API_URL}api/gift-cards-transactions/${tx._id}`,
        { status: "FAILED", rejectionReason: reason }
      );
    },
    onSuccess: () => {
      toast.success("Transaction rejected.");
      setRejectOpen(false);
      setRejectReason("");
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey("admin-transactions-data"),
      });
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey("admin-transactions-summaries"),
      });
      onClose();
    },
    onError: (e: unknown) => {
      const msg =
        axios.isAxiosError(e) &&
        e.response?.data &&
        typeof e.response.data === "object" &&
        "message" in e.response.data
          ? String((e.response.data as { message?: string }).message)
          : e instanceof Error
          ? e.message
          : "Could not reject transaction.";
      toast.error(msg);
    },
  });

  /**
   * Opens every proof image in the in-page viewer.
   *
   * This used to spawn one browser tab per image, which buried the order it was
   * meant to be checked against. Unsigned URLs are signed first so the viewer
   * always receives something it can actually render.
   */
  const viewAllSigned = async () => {
    if (!isAuthenticated) return;

    if (signedUrls.length > 0) {
      setProofIndex(0);
      return;
    }

    const urls = tx ? giftcardImageUrls(tx) : [];
    try {
      const signed = await Promise.all(
        urls.map((u) => requestSignedImageUrl(u, isAuthenticated))
      );
      setSignedUrls(signed);
      setProofIndex(0);
    } catch {
      toast.error("Could not open images.");
    }
  };

  if (!open || !tx) return null;

  const canConfirm =
    isPending &&
    math.ngn > 0 &&
    math.rateMatched &&
    email.length > 0 &&
    defaultBank != null;

  return (
    <div className="fixed inset-0 z-[155] flex items-end justify-center p-0 sm:items-center sm:p-6 font-manrope">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="giftcard-order-modal-title"
        className="relative z-[156] flex min-w-0 max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_-12px_48px_-16px_rgba(0,0,0,0.88)] sm:max-h-[90vh] sm:rounded-2xl sm:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {phase === "review" ? (
          <>
            <header className="shrink-0 border-b border-[#2C363E]/90 bg-gradient-to-br from-[#1a222d] via-[#141a20] to-[#13181B] px-4 pb-4 pt-4 sm:px-6 sm:pt-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${statusBadgeClass(
                        tx.status
                      )}`}
                    >
                      {str(tx.status) || "—"}
                    </span>
                    {isPending ? (
                      <span className="inline-flex rounded-full border border-amber-500/35 bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-amber-200">
                        Action needed
                      </span>
                    ) : null}
                  </div>
                  <h2
                    id="giftcard-order-modal-title"
                    className="font-sora text-lg font-bold leading-snug text-white sm:text-xl"
                  >
                    {isPending ? "Review gift card trade" : "Gift card trade"}
                    <span className="font-normal text-[#5C6B76]"> · </span>
                    <span className="font-mono text-[15px] font-semibold text-[#C6D7E4]">
                      {idShort}
                    </span>
                  </h2>
                  <p className="text-[12px] text-[#617889]">
                    {giftcardTypeLabel(tx.type)}
                    <span className="text-[#5C6B76]"> · </span>
                    {str(tx.country) || "—"}
                    <span className="text-[#5C6B76]"> · </span>
                    {getGiftcardDisplayDate(tx)}
                  </p>
                  <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center pt-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-[#617889]">
                      Trade ID
                    </span>
                    <CopyChip text={tx._id} label="Trade ID" />
                  </div>
                </div>
                <button
                  ref={closeRef}
                  type="button"
                  onClick={onClose}
                  className="shrink-0 rounded-xl border border-[#3A4852] bg-[#1C2227]/80 px-3.5 py-2 text-[12px] font-semibold text-[#E7EBEE] transition hover:border-[#5C6B76] hover:bg-[#273037]"
                >
                  Close
                </button>
              </div>
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 scrollbar-thin scrollbar-track-[#13181B] scrollbar-thumb-[#3A4852] sm:px-6">
              <div className="min-w-0 space-y-4">
                {isCompleted ? (
                  <div className="rounded-xl border border-emerald-500/35 bg-emerald-500/10 px-4 py-3 text-[13px] font-medium text-emerald-200">
                    Completed — payout was already finalized for this trade.
                    Details below are read-only.
                  </div>
                ) : null}

                {!math.rateMatched || math.ngn <= 0 ? (
                  <div className="rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 text-[12px] text-amber-100 leading-relaxed">
                    Rate matching incomplete: check country, card submission
                    type, and face-value ranges. Mark as paid stays disabled
                    until NGN &gt; 0 and rates match.
                  </div>
                ) : null}

                <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#161E25] p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                      Face (USD)
                    </p>
                    <p className="mt-2 min-w-0 font-sora text-xl font-bold tabular-nums leading-snug text-white sm:text-2xl [overflow-wrap:anywhere]">
                      ${formatAmount(math.usd)}
                    </p>
                  </div>
                  <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                      Payout (NGN)
                    </p>
                    <p className="mt-2 min-w-0 font-sora text-xl font-bold tabular-nums leading-snug text-[#BEE1FB] sm:text-2xl [overflow-wrap:anywhere]">
                      ₦{formatAmount(math.ngn)}
                    </p>
                  </div>
                  <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                      API totalTrade
                    </p>
                    <p className="mt-2 font-sora text-xl font-bold tabular-nums text-[#E7EBEE] sm:text-2xl">
                      {apiTotalTrade != null
                        ? `$${formatAmount(apiTotalTrade)}`
                        : "—"}
                    </p>
                  </div>
                </div>

                <p className="text-center text-[15px] font-semibold text-white">
                  {displayName}
                </p>

                <SectionCard title="Identifiers">
                  <KV
                    label="User ID (trade)"
                    value={
                      userId ? (
                        <div className="flex flex-col items-stretch gap-2 sm:items-end">
                          <span className="font-mono text-[12px] text-[#C6D7E4] break-all">
                            {userId}
                          </span>
                          <CopyChip text={userId} label="User ID" />
                        </div>
                      ) : (
                        "—"
                      )
                    }
                  />
                  {userMongoId && userMongoId !== userId ? (
                    <KV
                      label="User _id (embedded)"
                      value={
                        <div className="flex flex-col items-stretch gap-2 sm:items-end">
                          <span className="font-mono text-[12px] break-all text-[#C6D7E4]">
                            {userMongoId}
                          </span>
                          <CopyChip text={userMongoId} label="User _id" />
                        </div>
                      }
                    />
                  ) : null}
                  {cardCatalogId ? (
                    <KV
                      label="Card catalog ID"
                      value={
                        <div className="flex flex-col items-stretch gap-2 sm:items-end">
                          <span className="font-mono text-[11px] break-all text-[#C6D7E4]">
                            {cardCatalogId}
                          </span>
                          <CopyChip text={cardCatalogId} label="Card ID" />
                        </div>
                      }
                    />
                  ) : null}
                  <KV label="Created" value={formatTs(tx.createdAt)} />
                  <KV label="Updated" value={formatTs(tx.updatedAt)} />
                  {tx.__v != null ? (
                    <KV label="Version (__v)" value={String(tx.__v)} />
                  ) : null}
                </SectionCard>

                <SectionCard title="Product & submission">
                  <KV label="Card name" value={cardName} />
                  <KV label="Country" value={str(tx.country) || "—"} />
                  <KV
                    label="Submission type"
                    value={giftcardTypeLabel(tx.type)}
                  />
                </SectionCard>

                {tradeLegs.length > 0 ? (
                  <SectionCard title="Trade value (legs)">
                    <div className="overflow-x-auto rounded-lg border border-[#2C363E]/80 -mx-1 sm:mx-0">
                      <table className="w-full min-w-[280px] text-left text-[12px]">
                        <thead className="bg-[#0D1114]/90 text-[10px] font-bold uppercase tracking-wide text-[#617889]">
                          <tr>
                            <th className="px-3 py-2">Range min</th>
                            <th className="px-3 py-2">Range max</th>
                            <th className="px-3 py-2 text-right tabular-nums">
                              Amount (USD)
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-[#E7EBEE]">
                          {tradeLegs.map((row) => (
                            <tr
                              key={row.key}
                              className="border-t border-[#2C363E] bg-[#13181B]/30"
                            >
                              <td className="px-3 py-2 font-mono tabular-nums">
                                {row.min}
                              </td>
                              <td className="px-3 py-2 font-mono tabular-nums">
                                {row.max}
                              </td>
                              <td className="px-3 py-2 text-right font-mono tabular-nums">
                                {row.amount}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </SectionCard>
                ) : null}

                <SectionCard title="Proof">
                  {ecode ? (
                    <div className="py-3 space-y-2">
                      <p className="text-[11px] font-semibold text-[#7D8F9C]">
                        E-code
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-[12px] text-[#EEF2F6] break-all">
                          {ecode}
                        </span>
                        <CopyToClipboard
                          text={ecode}
                          onCopy={() => {
                            setCopyEcode(true);
                            toast.success("E-code copied");
                            window.setTimeout(() => setCopyEcode(false), 2000);
                          }}
                        >
                          <button
                            type="button"
                            className="rounded-lg border border-[#3A4852] px-2 py-1 text-[10px] font-bold uppercase text-[#AEBCC6]"
                          >
                            {copyEcode ? "Copied" : "Copy"}
                          </button>
                        </CopyToClipboard>
                      </div>
                    </div>
                  ) : (
                    <div className="py-3 space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[12px] text-[#7D8F9C]">
                          Images ({giftcardImageUrls(tx).length})
                        </span>
                        {signingImages ? (
                          <TailSpin color="#60B7F6" height={20} width={20} />
                        ) : (
                          <button
                            type="button"
                            onClick={() => void viewAllSigned()}
                            className="text-[12px] font-semibold text-[#4FB0FF] hover:underline"
                          >
                            View all
                          </button>
                        )}
                      </div>
                      {signedUrls.length > 0 ? (
                        <div className="grid grid-cols-2 gap-2">
                          {signedUrls.map((src, i) => (
                            <button
                              key={`${src}-${i}`}
                              type="button"
                              onClick={() => setProofIndex(i)}
                              className="block overflow-hidden rounded-lg border border-[#2C363E] bg-[#0D1114] aspect-video cursor-zoom-in transition-colors hover:border-[#4FB0FF]/60"
                            >
                              <img
                                src={src}
                                alt={`Proof ${i + 1}`}
                                className="h-full w-full object-contain"
                              />
                            </button>
                          ))}
                        </div>
                      ) : !signingImages ? (
                        <p className="text-[12px] text-[#617889]">
                          No images on this trade.
                        </p>
                      ) : null}
                    </div>
                  )}
                </SectionCard>

                <SectionCard title="Recipient & payout">
                  <KV
                    label="Email"
                    value={
                      <CopyToClipboard
                        text={email}
                        onCopy={() => {
                          setCopyMail(true);
                          window.setTimeout(() => setCopyMail(false), 2000);
                        }}
                      >
                        <button
                          type="button"
                          className="text-left text-[#4FB0FF] text-[13px] break-all sm:text-right"
                        >
                          {copyMail ? "Copied" : email || "—"}
                        </button>
                      </CopyToClipboard>
                    }
                  />
                  <KV
                    label="NGN payout amount"
                    value={
                      <CopyToClipboard
                        text={String(Math.round(math.ngn * 100) / 100)}
                        onCopy={() => {
                          setCopyAmount(true);
                          window.setTimeout(() => setCopyAmount(false), 2000);
                        }}
                      >
                        <button
                          type="button"
                          className="tabular-nums font-semibold text-white"
                        >
                          {copyAmount ? "Copied" : `₦${formatAmount(math.ngn)}`}
                        </button>
                      </CopyToClipboard>
                    }
                  />
                  <KV
                    label="Bank account"
                    value={
                      defaultBank ? (
                        <CopyToClipboard
                          text={String(defaultBank.accountNumber ?? "")}
                          onCopy={() => {
                            setCopyAccount(true);
                            window.setTimeout(
                              () => setCopyAccount(false),
                              2000
                            );
                          }}
                        >
                          <button
                            type="button"
                            className="flex flex-col items-end gap-0.5 text-right"
                          >
                            {copyAccount ? (
                              "Copied"
                            ) : (
                              <>
                                <span className="font-mono text-[13px] text-[#EEF2F6]">
                                  {String(defaultBank.accountNumber ?? "")}
                                </span>
                                <span className="text-[12px] text-[#869AA9]">
                                  {defaultBank.accountName} · {bankName}
                                </span>
                              </>
                            )}
                          </button>
                        </CopyToClipboard>
                      ) : (
                        <span className="text-[#869AA9]">—</span>
                      )
                    }
                  />
                </SectionCard>

                <div className="flex flex-col gap-3 pt-1 pb-2">
                  {isPending ? (
                    <button
                      type="button"
                      disabled={!canConfirm}
                      onClick={() => setPhase("confirm")}
                      className={
                        canConfirm
                          ? "flex h-12 items-center justify-center rounded-xl bg-secondary_button text-[14px] font-bold text-white transition hover:brightness-110"
                          : "flex h-12 cursor-not-allowed items-center justify-center rounded-xl bg-[#2C363E] text-[14px] font-bold text-[#5C6B76]"
                      }
                    >
                      Continue to mark as paid
                    </button>
                  ) : null}
                  {isPending ? (
                    <button
                      type="button"
                      onClick={() => setRejectOpen(true)}
                      className="flex h-12 items-center justify-center rounded-xl border border-red-500/35 bg-red-500/10 text-[14px] font-bold text-red-200 transition hover:border-red-400/60 hover:bg-red-500/15"
                    >
                      Reject transaction
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex max-h-[92vh] flex-col overflow-hidden">
            <div className="shrink-0 border-b border-[#2C363E] px-5 py-4 sm:px-6">
              <div className="flex items-center justify-between gap-3">
                <h3
                  id="giftcard-order-modal-title"
                  className="font-sora text-lg font-bold text-white"
                >
                  Mark as paid
                </h3>
                <button
                  type="button"
                  onClick={() => setPhase("review")}
                  className="text-[13px] font-semibold text-[#78C2F7] hover:text-[#A8D9FF]"
                >
                  Back
                </button>
              </div>
              <p className="mt-1 font-mono text-[11px] text-[#617889]">
                {tx._id}
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="flex gap-3 rounded-xl border border-[#2C363E] bg-[#0D1114]/60 p-4">
                <TiWarning className="shrink-0 text-2xl text-secondary_button" />
                <p className="text-[13px] leading-relaxed text-[#A0A9AD]">
                  Pay the user from your bank first, then use this to record it
                  in the system. Match profile name to the bank account name.
                  This creates a manual payout record and sets this trade to{" "}
                  <strong className="text-[#E7EBEE]">COMPLETED</strong>.
                </p>
              </div>
              <dl className="mt-4 space-y-2 rounded-xl border border-[#2C363E]/80 bg-[#161E25]/40 p-4 text-[13px]">
                <div className="flex justify-between gap-2">
                  <dt className="text-[#7D8F9C]">NGN</dt>
                  <dd className="font-semibold tabular-nums text-white">
                    ₦{formatAmount(math.ngn)}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-[#7D8F9C]">USD face</dt>
                  <dd className="tabular-nums text-[#BEE1FB]">
                    ${formatAmount(math.usd)}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-[#7D8F9C]">Card</dt>
                  <dd className="text-right font-medium text-white">
                    {cardName}
                  </dd>
                </div>
              </dl>
            </div>
            <div className="shrink-0 border-t border-[#2C363E] bg-[#13181B] px-5 py-4 sm:px-6">
              <div className="flex gap-3">
                <button
                  type="button"
                  disabled={confirmMutation.isPending}
                  onClick={() => confirmMutation.mutate()}
                  className="flex h-12 flex-1 items-center justify-center rounded-xl bg-secondary_button text-[14px] font-bold text-white transition hover:brightness-110 disabled:opacity-60"
                >
                  {confirmMutation.isPending ? (
                    <TailSpin color="#fff" height={26} width={26} />
                  ) : (
                    "Mark as paid"
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setPhase("review")}
                  className="flex h-12 flex-1 items-center justify-center rounded-xl border border-[#3A4852] text-[14px] font-bold text-[#AEBCC6] transition hover:bg-[#1C2227]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {rejectOpen ? (
        <div
          className="absolute inset-0 z-[160] flex items-center justify-center bg-black/70 p-4"
          onClick={() => {
            if (!rejectMutation.isPending) setRejectOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Reject transaction"
            className="w-full max-w-md rounded-2xl border border-[#2C363E] bg-[#13181B] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="font-sora text-[18px] font-bold text-white">
              Reject transaction
            </h4>
            <p className="mt-1 text-[12px] text-[#8EA3B2]">
              Enter a reason for rejecting this gift card transaction.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              placeholder="Type rejection reason"
              className="mt-4 w-full resize-none rounded-xl border border-[#3A4852] bg-[#0D1114]/80 px-3 py-2.5 text-[13px] text-[#E7EBEE] outline-none transition focus:border-red-400/70"
            />
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                disabled={rejectMutation.isPending}
                onClick={() => {
                  setRejectOpen(false);
                  setRejectReason("");
                }}
                className="flex h-11 flex-1 items-center justify-center rounded-xl border border-[#3A4852] text-[13px] font-bold text-[#AEBCC6] transition hover:bg-[#1C2227] disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={rejectMutation.isPending || !rejectReason.trim()}
                onClick={() => rejectMutation.mutate()}
                className="flex h-11 flex-1 items-center justify-center rounded-xl bg-red-600 text-[13px] font-bold text-white transition hover:bg-red-500 disabled:opacity-60"
              >
                {rejectMutation.isPending ? (
                  <TailSpin color="#fff" height={22} width={22} />
                ) : (
                  "Reject"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {proofIndex !== null && signedUrls.length > 0 && (
        <DocumentLightbox
          urls={signedUrls}
          startIndex={proofIndex}
          title="Proof of payment"
          onClose={() => setProofIndex(null)}
        />
      )}
    </div>
  );
};

export default AdminGiftcardOrderModal;
